/**
 * Wie dicht eine Ticketliste im Statusbericht gesetzt wird.
 *
 * Eigenes Modul neben `budget.ts` und `format.ts`, damit die Entscheidung
 * prüfbar ist: In `ReportView.tsx` steckte sie sonst mitten in einem
 * 1300-Zeilen-JSX-Baum, und dieses Projekt hat keine Rendering-Tests.
 *
 * **Der Anlass ist A4.** Entschieden ist: **alle** erledigten Vorgänge zeigen,
 * Vollständigkeit vor Lesbarkeit. Gemessen am ersten echten Leselauf (10.773
 * Vorgänge, 1.667 belegte Abteilung×Monat-Zellen) ist der Regelfall klein —
 * Median 3 erledigte Vorgänge je Kundenabteilung und Monat, oberes Zehntel 13.
 * Aber DENIOS SE lag im Mai, Juni und Juli 2026 bei 47, 57 und 60, also
 * **laufend**, nicht als historischer Ausrutscher.
 *
 * Der Block muss deshalb bei sechzig Zeilen noch lesbar sein, ohne eine Zeile
 * zu unterschlagen. Ab der Schwelle bekommt er: eine Kopfzeile, eine
 * Datumsspalte als Anker beim Überfliegen, engere Zeilen (die Liste wird um
 * gut ein Drittel kürzer) und einen Satz, der sagt, wonach sortiert ist.
 *
 * Was bewusst NICHT passiert:
 * - **kürzen oder deckeln** („und 40 weitere") — das verstieße gegen A4;
 * - **aufklappen** — der Bericht wird als HTML nach Outlook kopiert, dort gibt
 *   es kein JavaScript und kein `<details>`;
 * - **zwei Spalten nebeneinander** — Outlook kann das zwar, aber die Titel aus
 *   Jira sind zu lang; bei halber Breite bräche jede zweite Zeile um und die
 *   Liste würde länger statt kürzer.
 */

/**
 * Ab wie vielen Zeilen kompakt gesetzt wird.
 *
 * 15 — knapp über dem oberen Zehntel (13). Darunter ist die luftige
 * Darstellung die bessere; darüber trägt sie nicht mehr.
 */
export const TICKET_LIST_COMPACT_FROM = 15;

export interface TicketRowMetrics {
  /** Zellen-Innenabstand als CSS-Kurzform. */
  padding: string;
  /** Schriftgröße der Titelspalte in px. */
  fontSize: number;
  /** Kopfzeile und Datumsspalte zeigen? */
  compact: boolean;
}

/** Zeilenmaße für eine Ticketliste gegebener Länge. */
export function ticketRowMetrics(rowCount: number): TicketRowMetrics {
  const compact = rowCount >= TICKET_LIST_COMPACT_FROM;
  return compact
    ? { padding: "4px 12px", fontSize: 12, compact: true }
    : { padding: "9px 12px", fontSize: 13, compact: false };
}
